/**
 * Les pays de la caisse, leurs indicatifs et leurs portefeuilles — 11/10/2026.
 *
 * ## Pourquoi le pays remplace le portefeuille
 *
 * La fenêtre de paiement faisait choisir entre Wave, Orange Money et MTN MoMo,
 * puis titrait « Votre numéro Wave ». Ce choix n'orientait rien — chez Chariow,
 * le portefeuille se choisit sur sa page — et il mentait à la moitié de la
 * zone : un Togolais n'y trouvait ni Mixx by Yas ni Moov, les deux seuls qu'il
 * a. Le pays, lui, décide de quelque chose : de l'indicatif, donc de la caisse
 * que Chariow ouvre. Un Béninois qui tapait son numéro local à dix chiffres
 * était pris pour un Ivoirien et envoyé sur la caisse ivoirienne.
 *
 * ## D'où viennent les portefeuilles
 *
 * Relevés sur la caisse Chariow elle-même le 11/10/2026, pays par pays. Ils
 * sont affichés à titre indicatif : la page de l'opérateur fait foi, et le
 * dit. Les portefeuilles restent donc nommés à l'écran, comme le veut
 * l'arbitrage du 27/08 — par pays, au lieu d'une liste unique fausse ailleurs.
 */
export interface Pays {
  code: string;
  nom: string;
  indicatif: string;
  portefeuilles: readonly string[];
}

export const PAYS: readonly Pays[] = [
  { code: "CI", nom: "Côte d'Ivoire", indicatif: "225", portefeuilles: ["Wave", "Orange Money", "MTN", "Moov", "Djamo"] },
  { code: "BF", nom: "Burkina Faso", indicatif: "226", portefeuilles: ["Orange Money", "Wave", "Moov", "Telecel", "Coris", "Sank"] },
  { code: "TG", nom: "Togo", indicatif: "228", portefeuilles: ["Mixx by Yas", "Moov"] },
  { code: "BJ", nom: "Bénin", indicatif: "229", portefeuilles: ["MTN", "Moov", "Celtiis"] },
  { code: "SN", nom: "Sénégal", indicatif: "221", portefeuilles: ["Wave", "Orange Money", "Free Money", "Djamo"] },
  { code: "ML", nom: "Mali", indicatif: "223", portefeuilles: ["Orange Money", "Moov"] },
  { code: "CM", nom: "Cameroun", indicatif: "237", portefeuilles: ["Orange Money", "MTN"] },
  { code: "NE", nom: "Niger", indicatif: "227", portefeuilles: [] },
  { code: "GN", nom: "Guinée", indicatif: "224", portefeuilles: [] },
  { code: "GA", nom: "Gabon", indicatif: "241", portefeuilles: [] },
  { code: "CG", nom: "Congo", indicatif: "242", portefeuilles: [] },
  { code: "CD", nom: "RD Congo", indicatif: "243", portefeuilles: [] },
  { code: "TD", nom: "Tchad", indicatif: "235", portefeuilles: [] },
];

/** Les pays montrés en boutons ; les autres passent par la liste. */
export const PAYS_PRINCIPAUX = PAYS.slice(0, 4);

export function paysParCode(code: string): Pays | undefined {
  return PAYS.find((p) => p.code === code);
}

/**
 * Le numéro au format international, à partir de ce que la personne a tapé.
 *
 * - « +… » ou « 00… » : déjà international, pris tel quel.
 * - chiffres commençant par l'indicatif du pays choisi, assez longs pour en
 *   contenir un : déjà international sans le plus.
 * - sinon : numéro local, l'indicatif du pays choisi est ajouté. Le zéro de
 *   tête est gardé : il fait partie du numéro en Côte d'Ivoire et au Bénin.
 */
export function numeroInternational(saisie: string, pays: Pays): string {
  const brut = saisie.trim();
  const chiffres = brut.replace(/\D/g, "");
  if (brut.startsWith("+")) return `+${chiffres}`;
  if (chiffres.startsWith("00")) return `+${chiffres.slice(2)}`;
  if (chiffres.startsWith(pays.indicatif) && chiffres.length >= pays.indicatif.length + 8) {
    return `+${chiffres}`;
  }
  return `+${pays.indicatif}${chiffres}`;
}

/** Une phrase lisible : « Mixx by Yas, Moov ou carte bancaire ». */
export function phrasePortefeuilles(pays: Pays): string {
  const tous = [...pays.portefeuilles, "carte bancaire"];
  if (tous.length === 1) return tous[0];
  return `${tous.slice(0, -1).join(", ")} ou ${tous[tous.length - 1]}`;
}
